import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
    fetchArticlesByCategory,
    resetFetchedCategories,
    markCategoryFetched,
    setPage,
} from '../slices/siteArticlesSlice.mjs';
import { fetchBulkReactions } from '../slices/reactionsSlice.mjs';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import { setPageTitle, setPageDescription, setPageMetadata, setCanonicalUrl, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';
import HomePageContent from './body/Home.mjs';
import { setHeaderData } from '../slices/headerSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import useSafeSearchParams from '../hooks/useSafeSearchParams.mjs';



const HomePageWrapper = () => {
    const {
        articlesByCategory,
        isLoadingByCategory,
        errorByCategory,
        fetchParams,
        fetchedCategories,
        pagination,
    } = useSelector(
        (state) =>
            state.siteArticles || {
                articlesByCategory: [],
                isLoadingByCategory: false,
                errorByCategory: null,
                fetchParams: {},
                fetchedCategories: {},
                pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
            }
    );
    const { pureCategories } = useSelector((state) => state.headerSlice || { pureCategories: [] });

    // Get layout settings from Redux (set by middleware)
    const layoutSettings = useSelector((state) => state.pageLayoutSettings?.current);
    const articlesPerPage = layoutSettings?.pagination?.articlesPerPage || 25;

    // Add reactions state to selector
    const {
        byArticleId: reactionsByArticleId,
        loading: reactionsLoading,
        fetched: reactionsFetched,
    } = useSelector(
        (state) =>
            state.reactions || {
                byArticleId: {},
                loading: { bulk: false },
                fetched: {},
            }
    );

    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const [searchParams, setSearchParams] = useSafeSearchParams();

    // Current page from Redux (SSR-safe)
    const currentPage = pagination?.page || 1;

    // Extract all article IDs for bulk reaction loading
    const allArticleIds = useMemo(() => {
        if (!Array.isArray(articlesByCategory)) return [];

        return articlesByCategory.flatMap((group) =>
            group && Array.isArray(group.articles)
                ? group.articles.map((article) => article?._id).filter(Boolean)
                : []
        );
    }, [articlesByCategory]);

    // Determine which article IDs need reactions fetched
    const unfetchedReactionIds = useMemo(() => {
        return allArticleIds.filter(
            (articleId) => !reactionsFetched[articleId] && !reactionsByArticleId[articleId]
        );
    }, [allArticleIds, reactionsFetched, reactionsByArticleId]);

    // On mount, set page metadata and fetch articles if needed
    useEffect(() => {
        // Set metadata
        const pageTitle = 'Nexus of Truth';
        const pageDesc = 'Discover the truth through articles and insights.';
        dispatch(setPageTitle(pageTitle));
        dispatch(setPageDescription(pageDesc));
        const canonicalUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}/` : '';
        dispatch(setCanonicalUrl(canonicalUrl));
        // Extract first available article image for social cards
        let firstImage = null;
        if (Array.isArray(articlesByCategory)) {
            for (const group of articlesByCategory) {
                if (group?.articles?.length) {
                    for (const a of group.articles) {
                        const img = a?.generatedImages?.[0]?.url || a?.images?.[0]?.url;
                        if (img) { firstImage = img; break; }
                    }
                }
                if (firstImage) break;
            }
        }
        dispatch(setPageMetadata({
            'og:title': pageTitle,
            'og:description': pageDesc,
            'og:type': 'website',
            'og:url': canonicalUrl,
            ...(firstImage ? { 'og:image': firstImage } : {}),
            'twitter:card': firstImage ? 'summary_large_image' : 'summary',
            'twitter:title': pageTitle,
            'twitter:description': pageDesc,
            ...(firstImage ? { 'twitter:image': firstImage } : {}),
        }));
        dispatch(
            setHeaderData({
                title: pageTitle,
                description: pageDesc,
            })
        );

        // Fetch decision logic
        const allCategoryFetched = fetchedCategories['all']?.fetched === true;
        const allCategoryPending = fetchedCategories['all']?.pending === true;

        const fetchDecision = (() => {
            const isFetchingAllCategories = isLoadingByCategory && fetchParams?.category === 'all';
            const existingCategoriesInStore = articlesByCategory.map((group) => group.category);
            const allServerCategoriesPresent =
                pureCategories.length > 0
                    ? pureCategories.every((pc) => existingCategoriesInStore.includes(pc.name))
                    : true;
            const dataIsComplete =
                articlesByCategory.length > 0 &&
                allServerCategoriesPresent &&
                fetchParams?.category === 'all';

            // More defensive: don't fetch if already fetched (and not pending) OR if currently fetching OR if pending
            const shouldNotFetch =
                (allCategoryFetched && !allCategoryPending) ||
                isFetchingAllCategories ||
                allCategoryPending ||
                dataIsComplete;

            return {
                shouldFetch: !shouldNotFetch,
                isAlreadyFetching: isFetchingAllCategories || allCategoryPending,
                hasExistingData:
                    dataIsComplete || (articlesByCategory.length > 0 && allCategoryFetched && !allCategoryPending),
            };
        })();

        // Act based on fetch decision - ONLY handle articles, not reactions
        if (fetchDecision.shouldFetch) {
            console.log('HomePage: Fetching articles for "all" categories.');
            dispatch(
                fetchArticlesByCategory({
                    queryType: 'byCategory',
                    category: 'all',
                    limitPerCategory: articlesPerPage,
                })
            );
        } else if (fetchDecision.isAlreadyFetching) {
            console.log('HomePage: Already fetching "all" categories, waiting for completion...');
        } else if (fetchDecision.hasExistingData) {
            console.log('HomePage: Using preloaded/existing "all" articles.');

            // Mark as fetched if needed
            if (!allCategoryFetched && articlesByCategory.length > 0) {
                dispatch(markCategoryFetched({ category: 'all', success: true }));
            }

            // DON'T dispatch bulk reactions here - let the dedicated useEffect handle it
        }

        return () => {
            dispatch(resetPageMetadata());
        };
    }, [
        dispatch,
        articlesByCategory,
        pureCategories,
        fetchParams,
        fetchedCategories,
        isLoadingByCategory,
        // Remove reaction dependencies from this useEffect
    ]);

    // Smart bulk reactions fetching - only fetch for new articles
    useEffect(() => {
        // Only fetch if:
        // 1. We have unfetched article IDs
        // 2. We're not already loading bulk reactions
        // 3. We're not currently fetching articles (wait for articles to complete first)
        const shouldFetchReactions =
            unfetchedReactionIds.length > 0 && !reactionsLoading.bulk && !isLoadingByCategory;

        if (shouldFetchReactions) {
            console.log(
                `HomePage: Loading reactions for ${unfetchedReactionIds.length} new articles`
            );
            dispatch(fetchBulkReactions({ articleIds: unfetchedReactionIds }));
        }
    }, [unfetchedReactionIds, dispatch, reactionsLoading.bulk, isLoadingByCategory]);

    // Rating aggregates — separate effect so reaction state changes don't re-trigger
    useEffect(() => {
        if (allArticleIds.length > 0 && !isLoadingByCategory) {
            dispatch(fetchBulkRatingAggregates({ articleIds: allArticleIds }));
        }
    }, [allArticleIds, dispatch, isLoadingByCategory]);

    // Fetch articles periodically (every 5 minutes)
    useEffect(() => {
        const intervalId = setInterval(
            () => {
                // Check if we're currently fetching
                const isFetchingAllCategories =
                    isLoadingByCategory && fetchParams?.category === 'all';

                if (!isFetchingAllCategories) {
                    console.log('Refreshing articles for Home Page...');
                    dispatch(resetFetchedCategories());
                    dispatch(
                        fetchArticlesByCategory({
                            queryType: 'byCategory',
                            category: 'all',
                            limitPerCategory: articlesPerPage,
                        })
                    );
                    // Note: Reactions will be fetched automatically via the smart useEffect above
                    // when new articles are detected
                } else {
                    console.log('Skipping periodic refresh - fetch already in progress');
                }
            },
            60 * 60 * 1000
        ); // 1 hour interval

        return () => clearInterval(intervalId);
    }, [dispatch, isLoadingByCategory, fetchParams]);

    // UI state calculations
    const isFetchingAllCategories = isLoadingByCategory && fetchParams?.category === 'all';
    const articlesToDisplay = useMemo(() => articlesByCategory || [], [articlesByCategory]);
    const showLoading = isFetchingAllCategories && articlesToDisplay.length === 0;
    const noArticlesAvailable = !showLoading && articlesToDisplay.length === 0;
    const hasError = !!errorByCategory;

    // Sync URL page to Redux on mount and when URL changes (back/forward)
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    useEffect(() => {
        if (isMounted && urlPage !== currentPage) {
            dispatch(setPage(urlPage));
            dispatch(resetFetchedCategories());
            dispatch(
                fetchArticlesByCategory({
                    queryType: 'byCategory',
                    category: 'all',
                    page: urlPage,
                    limitPerCategory: articlesPerPage,
                })
            );
        }
    }, [urlPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle page change - update URL (enables browser history)
    const handlePageChange = useCallback((newPage, updateUrl = true) => {
        if (updateUrl) {
            setSearchParams({ page: newPage.toString() });
        }
        dispatch(setPage(newPage));
        dispatch(resetFetchedCategories());
        dispatch(
            fetchArticlesByCategory({
                queryType: 'byCategory',
                category: 'all',
                page: newPage,
                limitPerCategory: articlesPerPage,
            })
        );
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [setSearchParams, dispatch, articlesPerPage]);

    return (
        <main className="home-page-container">
            {showLoading ? (
                <div className="home-loading-container">
                    <div className="home-loading-spinner"></div>
                    <p>Loading the latest news...</p>
                </div>
            ) : hasError ? (
                <div className="home-error-message">
                    <h2>Error loading articles</h2>
                    <p>{errorByCategory?.toString() || 'An unknown error occurred'}</p>
                    <button
                        onClick={() => {
                            dispatch(resetFetchedCategories());
                            dispatch(
                                fetchArticlesByCategory({
                                    queryType: 'byCategory',
                                    category: 'all',
                                    limitPerCategory: articlesPerPage,
                                })
                            );
                        }}
                    >
                        Try Again
                    </button>
                </div>
            ) : noArticlesAvailable ? (
                <div className="home-no-articles-message">
                    <h2>No articles available</h2>
                    <p>Check back soon for the latest content.</p>
                </div>
            ) : (
                <HomePageContent
                    articlesGroupedByCategory={articlesToDisplay}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                />
            )}
        </main>
    );
};

export default HomePageWrapper;
